import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ChevronUp, ChevronDown, ChevronsUpDown, Trash2, RotateCcw, X,
  Filter, ChevronDown as ChevronDownSmall, Plus, Download, Loader2, Check, Shield, Inbox, UserPlus, UserMinus,
  Pencil,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import ModalPortal from "@/components/ModalPortal";
import SubTabs from "@/components/SubTabs";
import { LokationerAdmin } from "@/components/LokationerAdmin";
import AccessDenied from "@/components/AccessDenied";
import RettighedsEditor from "@/components/RettighedsEditor";
import { RETTIGHEDER_FALLBACK, aktiveRettigheder, rettighederTilDict } from "@/lib/rettigheder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLLE_BADGE = {
  owner:      { cls: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20", label: "SuperAdmin" },
  superadmin: { cls: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20", label: "SuperAdmin" },
  api_admin:  { cls: "bg-slate-200 text-slate-700 dark:bg-slate-500/10 dark:text-slate-300 border border-slate-300 dark:border-slate-500/20", label: "API admin" },
  admin:      { cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20", label: "Admin" },
  member:     { cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20", label: "Medarbejder" },
  light:      { cls: "bg-teal-500/10 text-teal-700 dark:text-teal-300 border border-teal-500/20", label: "Light" },
  guest:      { cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20", label: "Elev" },
};

function RolleBadge({ rolle }) {
  const r = ROLLE_BADGE[rolle];
  if (!r) return null;
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${r.cls}`}>{r.label}</span>;
}

const STATUS_STYLES = {
  "Aktiv":        { bg: "bg-green-500/10",  text: "text-green-500",                   dot: "bg-green-500" },
  "Fratrådt":     { bg: "bg-red-500/10",    text: "text-red-500",                     dot: "bg-red-500" },
  "Opsagt":       { bg: "bg-orange-500/10", text: "text-orange-500",                  dot: "bg-orange-500" },
  "Ophørt":       { bg: "bg-orange-500/10", text: "text-orange-500",                  dot: "bg-orange-500" },
  "Bortvist":     { bg: "bg-red-900/10",    text: "text-red-800 dark:text-red-300",   dot: "bg-red-800" },
  "Barsel/Orlov": { bg: "bg-purple-500/10", text: "text-purple-500",                  dot: "bg-purple-500" },
  // #38.5: kommende ansat (kontrakt underskrevet, startdato i fremtiden)
  "Starter":      { bg: "bg-blue-500/10",   text: "text-blue-600 dark:text-blue-400", dot: "bg-blue-500" },
};

const TIDLIGERE_STATUSSER = ["Fratrådt", "Bortvist", "Ophørt"];

const todayIso = new Date().toISOString().slice(0, 10);

// Opsagt med fremtidig ophørsdato tæller stadig som aktiv.
// Login-only brugere (elever/guests) har status=null men er aktive loginbrugere.
function erAktiv(m) {
  if (m._kun_login_bruger) return true;
  if (m.status === "Aktiv" || m.status === "Barsel/Orlov") return true;
  if (m.status === "Opsagt" && m.ophørsdato && m.ophørsdato > todayIso) return true;
  return false;
}

function fmtDatoShort(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

const ALLE_KOLONNER = [
  { key: "name",              label: "Navn" },
  { key: "status",            label: "Status" },
  { key: "stilling",          label: "Stilling" },
  { key: "afdeling",          label: "Afdeling" },
  { key: "email",             label: "Privat e-mail" },
  { key: "arbejds_email",     label: "Arbejds-e-mail" },
  { key: "telefon",           label: "Telefon" },
  { key: "adresse",           label: "Adresse" },
  { key: "ansaettelsesdato",  label: "Ansættelsesdato" },
  { key: "ansaettelsestype",  label: "Ansættelsestype" },
  { key: "ophørsdato",        label: "Ophørsdato" },
  { key: "kontonummer",       label: "Kontonummer" },
  { key: "pensionsaftale",    label: "Pensionsaftale" },
  { key: "timer_pr_uge",      label: "Timer/uge" },
  { key: "cpr",               label: "CPR-nummer" },
];

const DISPLAY_NAMES = { "Koebenhavn": "København" };
const displayCity = (c) => DISPLAY_NAMES[c] || c || "—";

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30" />;
  return sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
}

function ConfirmDialog({ name, onConfirm, onCancel, message, confirmLabel = "Slet", danger = true }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">{name}</h3>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="border border-border bg-background text-sm font-medium px-4 py-2 rounded-xl hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">Annuller</button>
          <button
            onClick={onConfirm}
            className={`text-sm font-semibold text-white px-4 py-2 rounded-xl hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 ${danger ? "bg-red-500" : "bg-[#FF2A6D]"}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function MultiSelectFilter({ label, options, selected, onChange, optionLabels }) {
  const [open, setOpen] = useState(false);
  const count = selected.length;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-[13px] font-semibold border transition-colors select-none
          ${count > 0 ? "bg-[#FF2A6D]/10 border-[#FF2A6D]/40 text-[#FF2A6D]" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}
      >
        {label}
        {count > 0 && <span className="bg-[#FF2A6D] text-white rounded-full w-4 h-4 flex items-center justify-center text-[13px] leading-none">{count}</span>}
        <ChevronDownSmall className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1.5 z-20 bg-background border border-border rounded-xl shadow-xl min-w-[160px] py-1 overflow-hidden">
            {options.map(opt => {
              const checked = selected.includes(opt);
              const displayLabel = optionLabels?.[opt] || opt;
              return (
                <button
                  key={opt}
                  onClick={() => onChange(checked ? selected.filter(s => s !== opt) : [...selected, opt])}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] hover:bg-secondary transition-colors text-left"
                >
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${checked ? "bg-[#FF2A6D] border-[#FF2A6D]" : "border-border"}`}>
                    {checked && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5 fill-white"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </span>
                  <span className="truncate">{displayLabel}</span>
                </button>
              );
            })}
            {selected.length > 0 && (
              <div className="border-t border-border mt-1 pt-1">
                <button onClick={() => onChange([])} className="w-full text-[13px] text-muted-foreground hover:text-foreground px-3 py-1.5 text-left">Nulstil</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Opret medarbejder modal ────────────────────────────────────────────────
const ALLE_BYER = ["København", "Aarhus", "Odense", "Aalborg", "Kolding", "Administration", "TNBC"];

// #61 DEL C: ansættelsestyper (radio) — kanoniske backend-værdier.
const OPRET_ANS_TYPER = [
  { v: "manedsloen", label: "Månedsløn" },
  { v: "timeloen",   label: "Timeløn" },
  { v: "vikar",      label: "Vikar" },
];

// #61 DEL C: status (radio) — kun Aktiv/Inaktiv ved oprettelse i hub.
const OPRET_STATUSSER = ["Aktiv", "Inaktiv"];

// #49.1/#61: alle roller i fast rækkefølge — Medarbejder (member) er default.
const LOGIN_ROLLER = [
  { v: "member", label: "Medarbejder" },
  { v: "light", label: "Medarbejder (light)" },
  { v: "admin", label: "Admin" },
  { v: "superadmin", label: "SuperAdmin" },
  { v: "api_admin", label: "API admin (skrivebeskyttet)" },
];

// Simpel e-mail-validering (client-side). Backend laver den autoritative check.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Generér en tilfældig ~12-tegns adgangskode (sikker via crypto når muligt).
function genererKode(laengde = 12) {
  const alfabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#%&*";
  let kode = "";
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const buf = new Uint32Array(laengde);
    window.crypto.getRandomValues(buf);
    for (let i = 0; i < laengde; i++) kode += alfabet[buf[i] % alfabet.length];
  } else {
    for (let i = 0; i < laengde; i++) kode += alfabet[Math.floor(Math.random() * alfabet.length)];
  }
  return kode;
}

function OpretModal({ token, onCreated, onClose }) {
  const [form, setForm] = useState({
    // GRUNDOPLYSNINGER
    name: "", adresse: "", telefon: "", email: "",
    arbejds_telefon: "", arbejds_email: "", foedselsdag: "", cpr: "",
    // ANSÆTTELSE
    afdeling: "", stilling: "", ansaettelsestype: "manedsloen",
    ansaettelsesdato: "", status: "Aktiv",
    // LOGIN-KONTO
    brugernavn: "", password: "", send_email: true,
    // ROLLE
    rolle: "member",
  });
  // Brugernavnet følger privat e-mail indtil brugeren selv redigerer det.
  const [brugernavnRedigeret, setBrugernavnRedigeret] = useState(false);
  const [rettigheder, setRettigheder] = useState({});
  const [visRettigheder, setVisRettigheder] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Privat e-mail styrer brugernavnet indtil brugeren overstyrer det manuelt.
  const setEmail = (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, email: v, brugernavn: brugernavnRedigeret ? f.brugernavn : v }));
  };
  const setBrugernavn = (e) => {
    setBrugernavnRedigeret(true);
    setForm(f => ({ ...f, brugernavn: e.target.value }));
  };

  const fyldKode = () => setForm(f => ({ ...f, password: genererKode(12) }));

  // Client-side validering: påkrævede felter + gyldig e-mail.
  const emailGyldig = EMAIL_RE.test(form.email.trim());
  const kanOprette =
    form.name.trim() &&
    emailGyldig &&
    form.afdeling &&
    form.stilling.trim() &&
    form.ansaettelsestype &&
    form.ansaettelsesdato &&
    form.status &&
    form.rolle &&
    form.password.trim();

  const handleCreate = async () => {
    if (!kanOprette || saving) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        adresse: form.adresse.trim(),
        telefon: form.telefon.trim(),
        email: form.email.trim().toLowerCase(),
        arbejds_telefon: form.arbejds_telefon.trim(),
        arbejds_email: form.arbejds_email.trim().toLowerCase(),
        foedselsdag: form.foedselsdag,
        cpr: form.cpr.trim(),
        afdeling: form.afdeling,
        stilling: form.stilling.trim(),
        ansaettelsestype: form.ansaettelsestype,
        ansaettelsesdato: form.ansaettelsesdato,
        status: form.status,
        rolle: form.rolle,
        rettigheder,
        brugernavn: (form.brugernavn.trim() || form.email.trim()).toLowerCase(),
        password: form.password,
        send_email: form.send_email,
      };
      const r = await axios.post(`${API}/admin/medarbejdere`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated(r.data.medarbejder || r.data);
      onClose();
      let besked = "Medarbejder oprettet";
      if (form.send_email) {
        besked += r.data?.email_sendt
          ? " · kode sendt på e-mail"
          : " · koden kunne ikke sendes (mail ikke konfigureret)";
      }
      alert(besked);
    } catch (e) {
      // 409 (dublet-email) returnerer backend-detaljen.
      alert(e?.response?.data?.detail || "Kunne ikke oprette medarbejder");
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]";
  const labelCls = "text-[13px] text-muted-foreground mb-1 block";
  const sektionCls = "text-[13px] font-semibold uppercase tracking-wide text-muted-foreground";

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background z-10 rounded-t-2xl">
          <h2 className="font-semibold text-sm">Opret ny medarbejder</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 flex flex-col gap-6">
          {/* ── 1. GRUNDOPLYSNINGER ─────────────────────────────────────── */}
          <section className="flex flex-col gap-3">
            <p className={sektionCls}>Grundoplysninger</p>
            <div>
              <label className={labelCls}>Navn *</label>
              <input value={form.name} onChange={set("name")} placeholder="Fornavn Efternavn" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Adresse</label>
              <input value={form.adresse} onChange={set("adresse")} placeholder="Vej, postnr. by" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Privat telefon</label>
                <input value={form.telefon} onChange={set("telefon")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Privat e-mail *</label>
                <input type="email" value={form.email} onChange={setEmail} placeholder="navn@eksempel.dk"
                  className={`${inputCls} ${form.email && !emailGyldig ? "border-red-500/60 focus:ring-red-500" : ""}`} />
                {form.email && !emailGyldig && (
                  <p className="text-[12px] text-red-500 mt-1">Ugyldig e-mailadresse.</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Arbejdstelefon</label>
                <input value={form.arbejds_telefon} onChange={set("arbejds_telefon")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Arbejds-e-mail</label>
                <input type="email" value={form.arbejds_email} onChange={set("arbejds_email")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Fødselsdag</label>
                <input type="date" value={form.foedselsdag} onChange={set("foedselsdag")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Personnummer</label>
                <input value={form.cpr} onChange={set("cpr")} placeholder="valgfri" className={inputCls} />
                <p className="text-[12px] text-muted-foreground mt-1">(kan også sættes senere under Fortrolige)</p>
              </div>
            </div>
          </section>

          {/* ── 2. ANSÆTTELSE ───────────────────────────────────────────── */}
          <section className="flex flex-col gap-3 border-t border-border pt-5">
            <p className={sektionCls}>Ansættelse</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Afdeling *</label>
                <select value={form.afdeling} onChange={set("afdeling")} className={inputCls}>
                  <option value="">— vælg —</option>
                  {ALLE_BYER.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Stilling *</label>
                <input value={form.stilling} onChange={set("stilling")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Ansættelsesdato *</label>
                <input type="date" value={form.ansaettelsesdato} onChange={set("ansaettelsesdato")} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Ansættelsestype *</label>
              <div className="flex flex-wrap gap-4">
                {OPRET_ANS_TYPER.map(t => (
                  <label key={t.v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="opret-anstype" checked={form.ansaettelsestype === t.v}
                      onChange={() => setForm(f => ({ ...f, ansaettelsestype: t.v }))} className="accent-[#FF2A6D]" />
                    <span className="text-sm">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Status *</label>
              <div className="flex flex-wrap gap-4">
                {OPRET_STATUSSER.map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="opret-status" checked={form.status === s}
                      onChange={() => setForm(f => ({ ...f, status: s }))} className="accent-[#FF2A6D]" />
                    <span className="text-sm">{s}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* ── 3. LOGIN-KONTO ──────────────────────────────────────────── */}
          <section className="flex flex-col gap-3 border-t border-border pt-5">
            <p className={sektionCls}>Login-konto</p>
            <div>
              <label className={labelCls}>Brugernavn</label>
              <input value={form.brugernavn} onChange={setBrugernavn} placeholder="udfyldes fra privat e-mail" className={inputCls} />
              <p className="text-[12px] text-muted-foreground mt-1">Følger privat e-mail indtil du retter det manuelt.</p>
            </div>
            <div>
              <label className={labelCls}>Adgangskode *</label>
              <div className="flex gap-2">
                <input type="text" value={form.password} onChange={set("password")} autoComplete="new-password"
                  placeholder="Vælg eller generér en kode" className={`${inputCls} font-mono`} />
                <button type="button" onClick={fyldKode}
                  className="shrink-0 px-3 h-9 rounded-lg text-[13px] font-semibold border border-[#FF2A6D]/40 text-[#FF2A6D] bg-[#FF2A6D]/10 hover:bg-[#FF2A6D]/20 transition-colors whitespace-nowrap">
                  Generér tilfældig kode
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.send_email}
                onChange={e => setForm(f => ({ ...f, send_email: e.target.checked }))} className="accent-[#FF2A6D] w-4 h-4" />
              <span className="text-sm">Send kode på e-mail</span>
            </label>
          </section>

          {/* ── 4. ROLLE ────────────────────────────────────────────────── */}
          <section className="flex flex-col gap-2 border-t border-border pt-5">
            <p className={sektionCls}>Rolle *</p>
            <div className="flex flex-col gap-1.5">
              {LOGIN_ROLLER.map(r => (
                <label key={r.v} className="flex items-center gap-2.5 cursor-pointer">
                  <input type="radio" name="opret-rolle" checked={form.rolle === r.v}
                    onChange={() => setForm(f => ({ ...f, rolle: r.v }))} className="accent-[#FF2A6D]" />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* ── 5. INDIVIDUELLE RETTIGHEDER (valgfri) ───────────────────── */}
          <section className="flex flex-col gap-3 border-t border-border pt-5">
            <button type="button" onClick={() => setVisRettigheder(v => !v)}
              className="flex items-center justify-between text-left">
              <span className={sektionCls}>Individuelle rettigheder</span>
              <span className="flex items-center gap-1 text-[13px] text-muted-foreground">
                <span className="normal-case font-normal">(kan udfyldes senere)</span>
                {visRettigheder ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </button>
            {visRettigheder && (
              <RettighedsEditor
                rolle={form.rolle}
                vaerdier={rettigheder}
                onChange={setRettigheder}
                token={token}
                brugerNavn={form.name}
                primaerAfdeling={form.afdeling}
                onRolleChange={(ny) => setForm(f => ({ ...f, rolle: ny }))}
              />
            )}
          </section>
        </div>

        <div className="flex justify-end gap-2 px-6 pb-5 pt-1 sticky bottom-0 bg-background z-10 rounded-b-2xl border-t border-border">
          <button onClick={onClose} className="border border-border bg-background text-sm font-medium px-4 py-2 rounded-xl hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">Annuller</button>
          <button onClick={handleCreate} disabled={saving || !kanOprette}
            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-4 h-4" />}
            Opret medarbejder
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─── Excel export modal ─────────────────────────────────────────────────────
function ExcelModal({ token, onClose }) {
  const [valgte, setValgte] = useState(["name", "status", "stilling", "afdeling", "email", "telefon", "ansaettelsesdato"]);
  const [loading, setLoading] = useState(false);

  const toggle = (key) => setValgte(v => v.includes(key) ? v.filter(k => k !== key) : [...v, key]);

  const handleDownload = async () => {
    if (!valgte.length) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/medarbejdere/eksport/excel?kolonner=${valgte.join(",")}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "medarbejdere.xlsx"; a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch { alert("Eksport fejlede"); }
    setLoading(false);
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-sm">Eksporter til Excel</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 flex flex-col gap-1.5 max-h-80 overflow-y-auto">
          {ALLE_KOLONNER.map(({ key, label }) => {
            const checked = valgte.includes(key);
            return (
              <button key={key} onClick={() => toggle(key)}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm hover:bg-secondary transition-colors text-left">
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-[#FF2A6D] border-[#FF2A6D]" : "border-border"}`}>
                  {checked && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5 fill-white"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </span>
                {label}
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 px-6 pb-5">
          <button onClick={onClose} className="border border-border bg-background text-sm font-medium px-4 py-2 rounded-xl hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">Annuller</button>
          <button onClick={handleDownload} disabled={loading || !valgte.length}
            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-4 h-4" />}
            Download
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// Lookup label/beskrivelse for rettighed-id baseret på fælles katalog.
const RETTIGHED_META = RETTIGHEDER_FALLBACK.reduce((acc, r) => {
  acc[r.id] = r;
  return acc;
}, {});

function RettighedPopup({ token, item_id, rolle, vaerdier, onUpdated, onRolleUpdated, onClose, brugerNavn }) {
  const [dict, setDict] = useState(() => rettighederTilDict(vaerdier));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${item_id}/rettigheder`, { rettigheder: dict }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onUpdated(item_id, aktiveRettigheder(dict));
      onClose();
    } catch {
      alert("Kunne ikke gemme rettigheder");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-border">
          <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Shield className="w-4 h-4" /> Rediger rettigheder
          </span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-secondary"><X className="w-4 h-4" /></button>
        </div>
        <RettighedsEditor rolle={rolle || "member"} vaerdier={dict} onChange={setDict} token={token} brugerNavn={brugerNavn}
          itemId={item_id} onRolleChange={(ny) => onRolleUpdated && onRolleUpdated(item_id, ny)} />
        <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annullér</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-1.5">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Gemmer..." : "Gem"}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─── Genansæt opsagt/arkiveret medarbejder ──────────────────────────────────
// Ansættelsestyper matcher backend-kontrakten + Stamdata-fanens kanoniske værdier.
const GENANSAET_ANS_TYPER = [
  { value: "manedsloen", label: "Månedsløn" },
  { value: "timeloen",   label: "Timeløn" },
  { value: "vikar",      label: "Vikar" },
];
const GENANSAET_TYPE_LABEL = GENANSAET_ANS_TYPER.reduce((acc, o) => { acc[o.value] = o.label; return acc; }, {});

function GenansaetDialog({ token, medarbejder, onClose, onGenansat }) {
  const m = medarbejder;
  const [datoVal, setDatoVal] = useState(todayIso);
  const [sammeBetingelser, setSammeBetingelser] = useState(true);
  const [stilling, setStilling] = useState(m.stilling || "");
  const [afdeling, setAfdeling] = useState(m.afdeling || "");
  const [ansaettelsestype, setAnsaettelsestype] = useState(
    GENANSAET_TYPE_LABEL[m.ansaettelsestype] ? m.ansaettelsestype : "manedsloen"
  );
  const [loen, setLoen] = useState(m.loen != null ? String(m.loen) : "");
  const [timerUge, setTimerUge] = useState(m.timer_pr_uge != null ? String(m.timer_pr_uge) : "");
  const [saving, setSaving] = useState(false);

  // Lille grå info-linje med det vi ved om den tidligere ansættelse.
  const tidligereInfo = [
    m.ansaettelsesdato ? `Ansat ${fmtDatoShort(m.ansaettelsesdato)}` : null,
    m.ophørsdato ? `ophørt ${fmtDatoShort(m.ophørsdato)}` : null,
    m.stilling || null,
    m.afdeling ? displayCity(m.afdeling) : null,
    m.ansaettelsestype ? (GENANSAET_TYPE_LABEL[m.ansaettelsestype] || m.ansaettelsestype) : null,
  ].filter(Boolean).join(" · ");

  const handleGenansaet = async () => {
    if (!datoVal) { alert("Vælg en genansættelses-dato."); return; }
    setSaving(true);
    try {
      const body = { genansaettelses_dato: datoVal, samme_betingelser: sammeBetingelser };
      if (!sammeBetingelser) {
        body.stilling = stilling.trim();
        body.afdeling = afdeling.trim();
        body.ansaettelsestype = ansaettelsestype;
        if (loen.trim() !== "") body.loen = Number(loen);
        if (timerUge.trim() !== "") body.timer_uge = Number(timerUge);
      }
      const r = await axios.post(`${API}/admin/medarbejdere/${m.item_id}/genansaet`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onGenansat(m, r.data);
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke genansætte medarbejderen");
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-sm">Genansæt {m.name || "medarbejder"}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          {tidligereInfo && (
            <p className="text-[13px] text-muted-foreground bg-secondary/50 rounded-lg px-3 py-2">
              Tidligere ansættelse: {tidligereInfo}
            </p>
          )}

          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Genansættelses-dato</label>
            <input type="date" value={datoVal} onChange={e => setDatoVal(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="radio" name="genansaet-betingelser" checked={sammeBetingelser}
                onChange={() => setSammeBetingelser(true)} className="mt-0.5 accent-[#FF2A6D]" />
              <span className="text-sm">Ja, brug samme stilling, løn, afdeling og timer</span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="radio" name="genansaet-betingelser" checked={!sammeBetingelser}
                onChange={() => setSammeBetingelser(false)} className="mt-0.5 accent-[#FF2A6D]" />
              <span className="text-sm">Nej, opret nye betingelser</span>
            </label>
          </div>

          {!sammeBetingelser && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-border pt-4">
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Stilling</label>
                <input value={stilling} onChange={e => setStilling(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Afdeling</label>
                <select value={ALLE_BYER.includes(afdeling) ? afdeling : ""} onChange={e => setAfdeling(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
                  <option value="">— vælg —</option>
                  {ALLE_BYER.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Ansættelsestype</label>
                <select value={ansaettelsestype} onChange={e => setAnsaettelsestype(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
                  {GENANSAET_ANS_TYPER.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Løn</label>
                <input type="number" value={loen} onChange={e => setLoen(e.target.value)} placeholder="0"
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
              </div>
              <div>
                <label className="text-[13px] text-muted-foreground mb-1 block">Timer pr. uge</label>
                <input type="number" value={timerUge} onChange={e => setTimerUge(e.target.value)} placeholder="37"
                  className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 pb-5">
          <button onClick={onClose} className="border border-border bg-background text-sm font-medium px-4 py-2 rounded-xl hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">Annullér</button>
          <button onClick={handleGenansaet} disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#FF2A6D] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Genansæt
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

export default function MedarbejderePage() {
  const { user, token, logout, hasRettighed } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(() => {
    const VALID = new Set(["aktive", "tidligere", "alle", "arkiv", "indstillinger"]);
    const t = searchParams.get("tab");
    return VALID.has(t) ? t : "aktive";
  });
  const [medarbejdere, setMedarbejdere] = useState([]);
  const [arkiv, setArkiv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [confirm, setConfirm] = useState(null);
  const [showOpret, setShowOpret] = useState(false);
  const [showExcel, setShowExcel] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [genansaetItem, setGenansaetItem] = useState(null);

  const [filterStatus, setFilterStatus] = useState([]);
  const [filterBy, setFilterBy] = useState([]);
  const [filterStilling, setFilterStilling] = useState([]);
  const [filterRolle, setFilterRolle] = useState(["member"]);
  const [rettighedPopup, setRettighedPopup] = useState(null);

  const isSuperAdmin = ["superadmin", "owner", "api_admin"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  // ── Generelle indstillinger ─────────────────────────────────────────────
  const [indstillingerSubTab, setIndstillingerSubTab] = useState("selskaber");
  const [selskaber, setSelskaber] = useState([]);
  const [selskaberLoading, setSelskaberLoading] = useState(false);
  const [selskaberError, setSelskaberError] = useState(null);
  const [selskabDialog, setSelskabDialog] = useState(null); // null | { mode: "opret"|"rediger", data: {} }
  const [selskabSaving, setSelskabSaving] = useState(false);
  const [selskabForm, setSelskabForm] = useState({ navn: "", cvr: "", kort_tekst: "" });
  const [selskabConfirmSlet, setSelskabConfirmSlet] = useState(null);

  const [skabeloner, setSkabeloner] = useState([]);
  const [skabelonerLoading, setSkabelonerLoading] = useState(false);
  const [skabelonDialog, setSkabelonDialog] = useState(null); // null | { mode: "opret"|"rediger", data: {} }
  const [skabelonSaving, setSkabelonSaving] = useState(false);
  const [skabelonForm, setSkabelonForm] = useState({ navn: "", ansaettelsestype: "", indhold: "" });
  const [skabelonConfirmSlet, setSkabelonConfirmSlet] = useState(null);

  const loadSelskaber = useCallback(() => {
    if (!isSuperAdmin) return;
    setSelskaberLoading(true);
    setSelskaberError(null);
    axios.get(`${API}/admin/selskaber`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSelskaber(r.data || []))
      .catch(() => setSelskaberError("Kunne ikke hente selskaber"))
      .finally(() => setSelskaberLoading(false));
  }, [token, isSuperAdmin]);

  const loadSkabeloner = useCallback(() => {
    if (!isSuperAdmin) return;
    setSkabelonerLoading(true);
    axios.get(`${API}/admin/kontraktskabeloner`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSkabeloner(r.data || []))
      .catch(() => {})
      .finally(() => setSkabelonerLoading(false));
  }, [token, isSuperAdmin]);

  useEffect(() => {
    if (tab === "indstillinger" && isSuperAdmin) {
      loadSelskaber();
      loadSkabeloner();
    }
  }, [tab, isSuperAdmin, loadSelskaber, loadSkabeloner]);

  const doOpretSelskab = async () => {
    setSelskabSaving(true);
    try {
      await axios.post(`${API}/admin/selskaber`, selskabForm, { headers: { Authorization: `Bearer ${token}` } });
      setSelskabDialog(null);
      loadSelskaber();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved oprettelse");
    } finally { setSelskabSaving(false); }
  };

  const doOpdaterSelskab = async () => {
    setSelskabSaving(true);
    try {
      await axios.put(`${API}/admin/selskaber/${selskabDialog.data.selskab_id}`, selskabForm, { headers: { Authorization: `Bearer ${token}` } });
      setSelskabDialog(null);
      loadSelskaber();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved opdatering");
    } finally { setSelskabSaving(false); }
  };

  const doSletSelskab = async (selskab) => {
    try {
      await axios.delete(`${API}/admin/selskaber/${selskab.selskab_id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadSelskaber();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kan ikke slette selskab");
    } finally { setSelskabConfirmSlet(null); }
  };

  const doOpretSkabelon = async () => {
    setSkabelonSaving(true);
    try {
      await axios.post(`${API}/admin/kontraktskabeloner`, skabelonForm, { headers: { Authorization: `Bearer ${token}` } });
      setSkabelonDialog(null);
      loadSkabeloner();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved oprettelse");
    } finally { setSkabelonSaving(false); }
  };

  const doOpdaterSkabelon = async () => {
    setSkabelonSaving(true);
    try {
      await axios.put(`${API}/admin/kontraktskabeloner/${skabelonDialog.data.skabelon_id}`, skabelonForm, { headers: { Authorization: `Bearer ${token}` } });
      setSkabelonDialog(null);
      loadSkabeloner();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fejl ved opdatering");
    } finally { setSkabelonSaving(false); }
  };

  const doSletSkabelon = async (skabelon) => {
    try {
      await axios.delete(`${API}/admin/kontraktskabeloner/${skabelon.skabelon_id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadSkabeloner();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kan ikke slette skabelon");
    } finally { setSkabelonConfirmSlet(null); }
  };
  // Brugere uden rolle men med kan_se_medarbejdere-rettighed har også adgang.
  // (hasRettighed returnerer true for admin/superadmin/owner automatisk — så
  // brug rå rettigheder-dict til check for medarbejdere uden rolle.)
  const harSeMedarbejdereRettighed = !!(user?.rettigheder?.kan_se_medarbejdere);
  // Alle ANSATTE (member-rolle og opefter) må se selve oversigten. Elever/guests
  // uden medarbejder-rolle blokeres fortsat.
  const erAnsat = ["member", "light", "admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  const hasReadAccess = erAnsat;
  // Adgang til den enkelte medarbejders STAMDATA-profil — kun superadmin/admin
  // eller medarbejder med kan_se_medarbejdere. Menige medarbejdere ser listen,
  // men navn/billede er IKKE et link til stamdata.
  const canSeStamdata = isSuperAdmin || isAdmin || harSeMedarbejdereRettighed;
  const handleLogout = () => { logout(); navigate("/login"); };

  const loadAktive = useCallback(() => {
    return axios.get(`${API}/admin/medarbejdere`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setMedarbejdere(r.data.medarbejdere || []));
  }, [token]);

  const loadArkiv = useCallback(() => {
    return axios.get(`${API}/admin/medarbejdere/arkiv`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setArkiv(r.data.medarbejdere || []));
  }, [token]);

  useEffect(() => {
    if (!user || !hasReadAccess) {
      // Vis AccessDenied i stedet for at redirecte — så får brugeren en
      // forklaring i stedet for en hvid skærm.
      setLoading(false);
      return;
    }
    setLoading(true);
    // Kun superadmin/owner må læse arkivet (backend kræver det).
    const loaders = isSuperAdmin ? [loadAktive(), loadArkiv()] : [loadAktive()];
    Promise.all(loaders).finally(() => setLoading(false));
  }, [user, hasReadAccess, isSuperAdmin, loadAktive, loadArkiv, token]);

  // Nulstil tab hvis bruger uden rettighed har en restriktiv tab valgt
  useEffect(() => {
    if (!isAdmin && !isSuperAdmin && (tab === "tidligere" || tab === "alle" || tab === "arkiv")) {
      setTab("aktive");
    } else if (!isSuperAdmin && tab === "arkiv") {
      setTab("aktive");
    }
  }, [isAdmin, isSuperAdmin, tab]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const doArkiver = async (item) => {
    await axios.post(`${API}/admin/medarbejdere/${item.item_id}/arkiver`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setMedarbejdere(prev => prev.filter(m => m.item_id !== item.item_id));
    await loadArkiv();
    setConfirm(null);
  };

  // #61 DEL A: Sæt en (fejlkoblet) medarbejder tilbage til Elev — fjern fra
  // medarbejdere. Kun SuperAdmin. Personen forsvinder fra listen ved success.
  const doFjernFraMedarbejdere = async (item) => {
    await axios.post(`${API}/admin/medarbejdere/${item.item_id}/fjern-fra-medarbejdere`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setMedarbejdere(prev => prev.filter(m => m.item_id !== item.item_id));
    setConfirm(null);
  };

  const doSletFraArkiv = async (item) => {
    await axios.delete(`${API}/admin/medarbejdere/arkiv/${item.item_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setArkiv(prev => prev.filter(m => m.item_id !== item.item_id));
    setConfirm(null);
  };

  const doGendan = async (item) => {
    await axios.post(`${API}/admin/medarbejdere/arkiv/${item.item_id}/gendan`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setArkiv(prev => prev.filter(m => m.item_id !== item.item_id));
    await loadAktive();
    setConfirm(null);
  };

  // Efter en vellykket genansættelse: medarbejderen er nu "Aktiv". Genindlæs
  // listerne så vedkommende forsvinder fra Tidligere/Arkiv og dukker op i Aktive.
  const handleGenansat = async (item) => {
    // Optimistisk lokal opdatering så fanen straks afspejler ændringen.
    setMedarbejdere(prev => prev.map(m => m.item_id === item.item_id ? { ...m, status: "Aktiv" } : m));
    setArkiv(prev => prev.filter(m => m.item_id !== item.item_id));
    const loaders = isSuperAdmin ? [loadAktive(), loadArkiv()] : [loadAktive()];
    await Promise.all(loaders).catch(() => {});
    alert("Medarbejderen er genansat.");
  };

  const [syncMsg, setSyncMsg] = useState(null);

  const doMigrer = async () => {
    setMigrating(true);
    setSyncMsg("Starter sync...");
    try {
      await axios.post(`${API}/admin/medarbejdere/migrer-fra-podio`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSyncMsg("Sync kører i baggrunden...");
      // Poll status hvert 3. sekund indtil done/error
      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/medarbejdere/migrer-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const job = s.data;
          if (job.status === "done") {
            clearInterval(poll);
            setSyncMsg(`Sync færdig: ${job.imported} importeret, ${job.skipped} opdateret.`);
            setMigrating(false);
            await loadAktive();
          } else if (job.status === "error") {
            clearInterval(poll);
            setSyncMsg(`Sync fejlede: ${job.error}`);
            setMigrating(false);
          } else {
            setSyncMsg(`Sync kører... (${job.imported || 0} importeret)`);
          }
        } catch (_) {}
      }, 3000);
    } catch (e) {
      setSyncMsg("Sync fejlede: " + (e.response?.data?.detail || e.message));
      setMigrating(false);
    }
  };

  // #39.2: opdater rolle-badge lokalt når rollen ændres i rettigheds-popup.
  const updateRolleLocal = (item_id, nyRolle) => {
    setMedarbejdere(prev => prev.map(m => m.item_id === item_id ? { ...m, rolle: nyRolle } : m));
  };
  const updateRettighedLocal = (item_id, ny) => {
    setMedarbejdere(prev => prev.map(m => m.item_id === item_id ? { ...m, rettigheder: ny } : m));
  };

  // Tabulér medarbejdere — elever (_kun_login_bruger) vises i Alle-tab men ikke i Aktive/Tidligere
  const aktiveList    = useMemo(() => medarbejdere.filter(m => !m._kun_login_bruger && erAktiv(m)), [medarbejdere]);
  const tidligereList = useMemo(() => medarbejdere.filter(m => !m._kun_login_bruger && !erAktiv(m) && (TIDLIGERE_STATUSSER.includes(m.status) || m.status === "Opsagt")), [medarbejdere]);
  const alleList      = useMemo(() => medarbejdere, [medarbejdere]);

  // Tab-antal filtreret på rolle/by/status/stilling — matcher hvad brugeren ser
  const _matcherFiltre = useCallback((m) => {
    if (filterRolle.length > 0) {
      const effectiveRolle = m.rolle === "owner" ? "superadmin" : m.rolle;
      if (!filterRolle.includes(effectiveRolle)) return false;
    }
    if (filterStatus.length > 0 && !filterStatus.includes(m.status)) return false;
    if (filterBy.length > 0 && !filterBy.includes(m.afdeling)) return false;
    if (filterStilling.length > 0 && !filterStilling.includes(m.stilling)) return false;
    return true;
  }, [filterRolle, filterStatus, filterBy, filterStilling]);

  const aktiveCount   = useMemo(() => aktiveList.filter(_matcherFiltre).length, [aktiveList, _matcherFiltre]);
  const tidligereCount = useMemo(() => tidligereList.filter(_matcherFiltre).length, [tidligereList, _matcherFiltre]);
  const alleCount     = useMemo(() => alleList.filter(_matcherFiltre).length, [alleList, _matcherFiltre]);
  const arkivCount    = useMemo(() => arkiv.filter(_matcherFiltre).length, [arkiv, _matcherFiltre]);

  const list = tab === "aktive" ? aktiveList : tab === "tidligere" ? tidligereList : tab === "alle" ? alleList : arkiv;

  const allStatuser = useMemo(() => [...new Set(list.map(m => m.status).filter(Boolean))].sort(), [list]);
  const allByer = useMemo(() => [...new Set(list.map(m => m.afdeling).filter(Boolean))].sort(), [list]);
  const allStillinger = useMemo(() => [...new Set(list.map(m => m.stilling).filter(Boolean))].sort(), [list]);
  const allRoller = useMemo(() => {
    // owner og superadmin har samme label — vis kun superadmin som filter-option
    const rolleOrder = ["superadmin", "api_admin", "admin", "member", "light", "guest"];
    const present = new Set(medarbejdere.map(m => {
      const r = m.rolle;
      return r === "owner" ? "superadmin" : r;
    }).filter(Boolean));
    return rolleOrder.filter(r => present.has(r));
  }, [medarbejdere]);

  const anyFilter = filterStatus.length > 0 || filterBy.length > 0 || filterStilling.length > 0 || filterRolle.length > 0;

  const filtered = useMemo(() => list.filter(m => {
    if (filterStatus.length > 0 && !filterStatus.includes(m.status)) return false;
    if (filterBy.length > 0 && !filterBy.includes(m.afdeling)) return false;
    if (filterStilling.length > 0 && !filterStilling.includes(m.stilling)) return false;
    if (filterRolle.length > 0) {
      // owner behandles som superadmin i filteret
      const effectiveRolle = m.rolle === "owner" ? "superadmin" : m.rolle;
      if (!filterRolle.includes(effectiveRolle)) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!m.name?.toLowerCase().includes(q) && !m.stilling?.toLowerCase().includes(q) && !m.afdeling?.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [list, filterStatus, filterBy, filterStilling, filterRolle, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const av = (a[sortCol] || "").toString().toLowerCase();
    const bv = (b[sortCol] || "").toString().toLowerCase();
    return sortDir === "asc" ? av.localeCompare(bv, "da") : bv.localeCompare(av, "da");
  }), [filtered, sortCol, sortDir]);

  // Navigation til stamdata-profilen kræver stamdata-adgang. Menige medarbejdere
  // kan se listen, men ikke åbne/redigere et stamkort — så linket er inaktivt.
  const canNavigate = canSeStamdata && tab !== "arkiv";

  const Th = ({ col, label }) => (
    <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => handleSort(col)}>
      <span className="flex items-center gap-1">{label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir} /></span>
    </th>
  );

  if (user && !hasReadAccess) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <DashboardHeader user={user} onLogout={handleLogout} />
        <AccessDenied
          role="admin"
          title="Adgang til medarbejderlisten kræver rettighed"
          message="Du har ikke rettighed til at se medarbejderlisten. Kontakt en superadmin hvis du har brug for adgang."
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />

      {showOpret && <OpretModal token={token} onCreated={m => { setMedarbejdere(prev => [m, ...prev]); }} onClose={() => setShowOpret(false)} />}
      {showExcel && <ExcelModal token={token} onClose={() => setShowExcel(false)} />}
      {genansaetItem && (
        <GenansaetDialog
          token={token}
          medarbejder={genansaetItem}
          onClose={() => setGenansaetItem(null)}
          onGenansat={(item) => handleGenansat(item)}
        />
      )}

      {confirm?.type === "arkiver" && (
        <ConfirmDialog name={confirm.item.name} message="Medarbejderen fjernes fra listen og flyttes til arkivet. Du kan gendanne dem herfra."
          confirmLabel="Flyt til arkiv" onConfirm={() => doArkiver(confirm.item)} onCancel={() => setConfirm(null)} />
      )}
      {confirm?.type === "slet" && (
        <ConfirmDialog name={confirm.item.name} message="Medarbejderen slettes permanent fra arkivet. Dette kan ikke fortrydes."
          confirmLabel="Slet permanent" onConfirm={() => doSletFraArkiv(confirm.item)} onCancel={() => setConfirm(null)} />
      )}
      {/* #61 DEL A: Fjern fra medarbejdere → tilbage til Elev (kun SuperAdmin). */}
      {confirm?.type === "fjern" && (
        <ConfirmDialog name={confirm.item.name}
          message={`Sæt ${confirm.item.name || "personen"} tilbage til Elev? Personen fjernes fra medarbejdere.`}
          confirmLabel="Fjern fra medarbejdere" onConfirm={() => doFjernFraMedarbejdere(confirm.item)} onCancel={() => setConfirm(null)} />
      )}

      {/* #61 DEL A / #39.2: Ret rolle + rettigheder via eksisterende popup. */}
      {rettighedPopup && (
        <RettighedPopup
          token={token}
          item_id={rettighedPopup.item_id}
          rolle={rettighedPopup.rolle || "member"}
          vaerdier={rettighedPopup.rettigheder}
          brugerNavn={rettighedPopup.name || ""}
          onUpdated={updateRettighedLocal}
          onRolleUpdated={updateRolleLocal}
          onClose={() => setRettighedPopup(null)}
        />
      )}

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-16">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight mb-1">Brugere</h1>
            <p className="text-sm text-muted-foreground">Administrer brugerdata</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Excel-eksport af stamdata kun for superadmin (backend gater dette). */}
            {isSuperAdmin && (
              <button
                onClick={() => setShowExcel(true)}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-semibold border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              >
                <Download className="w-3.5 h-3.5" /> Excel
              </button>
            )}
            {isSuperAdmin && (
              <button
                onClick={() => setShowOpret(true)}
                className="flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
              >
                <Plus className="w-3.5 h-3.5" /> Opret ny
              </button>
            )}
          </div>
        </div>

        {/* Tabs — fælles SubTabs-skabelon. */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <SubTabs
            tabs={[
              { key: "aktive", label: anyFilter ? `Aktive (${aktiveCount}/${aktiveList.length})` : `Aktive (${aktiveList.length})` },
              // Tidligere og Alle kun for admin/superadmin
              ...((isAdmin || isSuperAdmin) ? [
                { key: "tidligere", label: anyFilter ? `Tidligere (${tidligereCount}/${tidligereList.length})` : `Tidligere (${tidligereList.length})` },
                { key: "alle",      label: anyFilter ? `Alle (${alleCount}/${alleList.length})` : `Alle (${alleList.length})` },
              ] : []),
              // Arkiv kun for superadmin/owner (backend kræver det)
              ...(isSuperAdmin ? [{ key: "arkiv", label: anyFilter ? `Arkiv (${arkivCount}/${arkiv.length})` : `Arkiv (${arkiv.length})` }] : []),
              // Generelle indstillinger kun for superadmin
              ...(isSuperAdmin ? [{ key: "indstillinger", label: "Generelle indstillinger" }] : []),
            ]}
            value={tab}
            onChange={(key) => { setTab(key); setFilterStatus([]); setFilterBy([]); setFilterStilling([]); setFilterRolle(key === "alle" || key === "arkiv" || key === "indstillinger" ? [] : ["member"]); setSearch(""); setSearchParams(p => { const n = new URLSearchParams(p); n.set("tab", key); return n; }, { replace: true }); }}
            className="w-full sm:w-auto"
          />

          {/* Migration / re-sync fra Podio — kun superadmin. Skjult på mobil. */}
          {isSuperAdmin && (
          <div className="hidden sm:flex w-full sm:w-auto sm:ml-auto items-center gap-2 min-w-0">
            {syncMsg && (
              <span className="text-[13px] text-muted-foreground max-w-[60%] sm:max-w-xs truncate">{syncMsg}</span>
            )}
            <button onClick={doMigrer} disabled={migrating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-semibold border border-amber-500/40 text-amber-500 bg-amber-500/10 hover:bg-amber-500/20 transition-colors disabled:opacity-60 shrink-0 ml-auto sm:ml-0">
              {migrating ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Sync fra Podio
            </button>
          </div>
          )}
        </div>

        {/* Søgning + filtre — skjult på indstillinger-tab */}
        {tab !== "indstillinger" && <div className="flex flex-wrap items-center gap-2 mb-4">
          <input
            type="text"
            placeholder="Søg på navn, stilling eller by..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-secondary border border-border rounded-lg px-3 h-9 sm:h-8 text-sm text-foreground w-full sm:max-w-xs focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            {/* Status-filter kun for dem der ser Status-kolonnen. */}
            {canSeStamdata && (
              <MultiSelectFilter label="Status" options={allStatuser} selected={filterStatus} onChange={setFilterStatus} />
            )}
            <MultiSelectFilter label="By" options={allByer} selected={filterBy} onChange={setFilterBy} />
            <MultiSelectFilter label="Stilling" options={allStillinger} selected={filterStilling} onChange={setFilterStilling} />
            <MultiSelectFilter
              label="Rolle"
              options={allRoller}
              selected={filterRolle}
              onChange={setFilterRolle}
              optionLabels={Object.fromEntries(Object.entries(ROLLE_BADGE).map(([k, v]) => [k, v.label]))}
            />
            {anyFilter && (
              <button onClick={() => { setFilterStatus([]); setFilterBy([]); setFilterStilling([]); setFilterRolle([]); }}
                className="text-[13px] text-muted-foreground hover:text-foreground transition-colors px-2 h-8 flex items-center gap-1">
                <X className="w-3 h-3" /> Ryd filtre
              </button>
            )}
          </div>
          {anyFilter && <span className="text-[13px] text-muted-foreground w-full sm:w-auto sm:ml-auto">Viser {sorted.length} af {list.length}</span>}
        </div>}

        {/* ── Generelle indstillinger ──────────────────────────────────── */}
        {tab === "indstillinger" && isSuperAdmin && (
          <div className="flex flex-col gap-6">
            {/* Sub-tabs: Selskaber / Kontraktskabeloner */}
            <div className="flex items-center gap-2 border-b border-border pb-0">
              {["selskaber", "kontraktskabeloner"].map(st => (
                <button key={st}
                  onClick={() => setIndstillingerSubTab(st)}
                  className={`px-4 py-2 text-[13px] font-semibold border-b-2 transition-colors -mb-px ${indstillingerSubTab === st ? "border-[#FF2A6D] text-[#FF2A6D]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {st === "selskaber" ? "Selskaber" : "Kontraktskabeloner"}
                </button>
              ))}
            </div>

            {/* ── Selskaber ── */}
            {indstillingerSubTab === "selskaber" && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold">Selskaber</h2>
                    <p className="text-[13px] text-muted-foreground mt-0.5">Selskaber der kan knyttes til medarbejderes ansættelse</p>
                  </div>
                  <button onClick={() => { setSelskabForm({ navn: "", cvr: "", kort_tekst: "" }); setSelskabDialog({ mode: "opret", data: {} }); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity">
                    <Plus className="w-3.5 h-3.5" /> Tilføj selskab
                  </button>
                </div>

                {selskaberLoading ? (
                  <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-[13px]">Henter selskaber...</span>
                  </div>
                ) : selskaberError ? (
                  <p className="text-[13px] text-red-500 py-4">{selskaberError}</p>
                ) : selskaber.length === 0 ? (
                  <div className="rounded-xl border border-border p-8 text-center text-muted-foreground">
                    <p className="text-[13px]">Ingen selskaber oprettet endnu</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-secondary/50">
                          <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Navn</th>
                          <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">CVR</th>
                          <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Kort tekst</th>
                          <th className="py-3 px-4 w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {selskaber.map((s, i) => (
                          <tr key={s.selskab_id} className={`border-b border-border ${i % 2 === 0 ? "" : "bg-secondary/10"}`}>
                            <td className="py-3 px-4 font-medium">{s.navn}</td>
                            <td className="py-3 px-4 text-muted-foreground">{s.cvr || "—"}</td>
                            <td className="py-3 px-4 text-muted-foreground">{s.kort_tekst || "—"}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => { setSelskabForm({ navn: s.navn, cvr: s.cvr || "", kort_tekst: s.kort_tekst || "" }); setSelskabDialog({ mode: "rediger", data: s }); }}
                                  title="Rediger"
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-[#FF2A6D] hover:bg-[#FF2A6D]/10 transition-colors">
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => setSelskabConfirmSlet(s)}
                                  title="Slet"
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Dialog: Opret/Rediger selskab */}
                {selskabDialog && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSelskabDialog(null)}>
                    <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                      <h3 className="text-base font-bold mb-4">{selskabDialog.mode === "opret" ? "Tilføj selskab" : "Rediger selskab"}</h3>
                      <div className="flex flex-col gap-3">
                        <div>
                          <label className="text-[13px] font-medium text-foreground block mb-1">Navn <span className="text-red-500">*</span></label>
                          <input type="text" value={selskabForm.navn} onChange={e => setSelskabForm(f => ({ ...f, navn: e.target.value }))}
                            placeholder="Virksomhedsnavn"
                            className="w-full h-9 rounded-xl border border-border bg-background px-3 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                        <div>
                          <label className="text-[13px] font-medium text-foreground block mb-1">CVR (8 cifre)</label>
                          <input type="text" inputMode="numeric" maxLength={8} value={selskabForm.cvr} onChange={e => setSelskabForm(f => ({ ...f, cvr: e.target.value.replace(/\D/g,"") }))}
                            placeholder="12345678"
                            className="w-full h-9 rounded-xl border border-border bg-background px-3 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                        <div>
                          <label className="text-[13px] font-medium text-foreground block mb-1">
                            Kort tekst <span className="text-muted-foreground font-normal">(max 20 tegn)</span>
                          </label>
                          <input type="text" maxLength={20} value={selskabForm.kort_tekst} onChange={e => setSelskabForm(f => ({ ...f, kort_tekst: e.target.value }))}
                            placeholder="Fx. lønseddel-tekst"
                            className="w-full h-9 rounded-xl border border-border bg-background px-3 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                          <p className="text-[12px] text-muted-foreground mt-0.5 text-right">{selskabForm.kort_tekst.length}/20</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-5">
                        <button onClick={() => setSelskabDialog(null)} className="px-4 py-2 rounded-xl border border-border text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">Annuller</button>
                        <button onClick={selskabDialog.mode === "opret" ? doOpretSelskab : doOpdaterSelskab}
                          disabled={selskabSaving || !selskabForm.navn.trim()}
                          className="px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                          {selskabSaving ? "Gemmer..." : selskabDialog.mode === "opret" ? "Opret" : "Gem"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bekræft slet selskab */}
                {selskabConfirmSlet && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSelskabConfirmSlet(null)}>
                    <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
                      <h3 className="text-base font-bold mb-2">Slet selskab?</h3>
                      <p className="text-[13px] text-muted-foreground mb-4">Er du sikker på at du vil slette <strong>{selskabConfirmSlet.navn}</strong>? Dette kan ikke fortrydes.</p>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setSelskabConfirmSlet(null)} className="px-4 py-2 rounded-xl border border-border text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">Annuller</button>
                        <button onClick={() => doSletSelskab(selskabConfirmSlet)} className="px-4 py-2 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:opacity-90 transition-opacity">Slet</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Kontraktskabeloner ── */}
            {indstillingerSubTab === "kontraktskabeloner" && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold">Kontraktskabeloner</h2>
                    <p className="text-[13px] text-muted-foreground mt-0.5">Skabeloner til generering af ansættelseskontrakter</p>
                  </div>
                  <button onClick={() => { setSkabelonForm({ navn: "", ansaettelsestype: "", indhold: "" }); setSkabelonDialog({ mode: "opret", data: {} }); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity">
                    <Plus className="w-3.5 h-3.5" /> Tilføj skabelon
                  </button>
                </div>

                {skabelonerLoading ? (
                  <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-[13px]">Henter skabeloner...</span>
                  </div>
                ) : skabeloner.length === 0 ? (
                  <div className="rounded-xl border border-border p-8 text-center text-muted-foreground">
                    <p className="text-[13px]">Ingen kontraktskabeloner oprettet endnu</p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-secondary/50">
                          <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Navn</th>
                          <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Ansættelsestype</th>
                          <th className="py-3 px-4 w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {skabeloner.map((s, i) => (
                          <tr key={s.skabelon_id} className={`border-b border-border ${i % 2 === 0 ? "" : "bg-secondary/10"}`}>
                            <td className="py-3 px-4 font-medium">{s.navn}</td>
                            <td className="py-3 px-4 text-muted-foreground">{s.ansaettelsestype || "—"}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => { setSkabelonForm({ navn: s.navn, ansaettelsestype: s.ansaettelsestype || "", indhold: s.indhold || "" }); setSkabelonDialog({ mode: "rediger", data: s }); }}
                                  title="Rediger"
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-[#FF2A6D] hover:bg-[#FF2A6D]/10 transition-colors">
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => setSkabelonConfirmSlet(s)}
                                  title="Slet"
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Dialog: Opret/Rediger skabelon */}
                {skabelonDialog && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSkabelonDialog(null)}>
                    <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
                      <h3 className="text-base font-bold mb-4">{skabelonDialog.mode === "opret" ? "Tilføj kontraktskabelon" : "Rediger kontraktskabelon"}</h3>
                      <div className="flex flex-col gap-3">
                        <div>
                          <label className="text-[13px] font-medium text-foreground block mb-1">Navn <span className="text-red-500">*</span></label>
                          <input type="text" value={skabelonForm.navn} onChange={e => setSkabelonForm(f => ({ ...f, navn: e.target.value }))}
                            placeholder="Skabelonnavn"
                            className="w-full h-9 rounded-xl border border-border bg-background px-3 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                        <div>
                          <label className="text-[13px] font-medium text-foreground block mb-1">Ansættelsestype</label>
                          <input type="text" value={skabelonForm.ansaettelsestype} onChange={e => setSkabelonForm(f => ({ ...f, ansaettelsestype: e.target.value }))}
                            placeholder="Fx. Månedsløn, Timeløn, Vikar..."
                            className="w-full h-9 rounded-xl border border-border bg-background px-3 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                        </div>
                        <div>
                          <label className="text-[13px] font-medium text-foreground block mb-1">Indhold</label>
                          <p className="text-[12px] text-muted-foreground mb-1.5">
                            Fletfelter: <code className="bg-secondary px-1 rounded">[Medarbejdernavn]</code> <code className="bg-secondary px-1 rounded">[Startdato]</code> <code className="bg-secondary px-1 rounded">[Ansættelsestype]</code> <code className="bg-secondary px-1 rounded">[Afdeling]</code>
                          </p>
                          <textarea value={skabelonForm.indhold} onChange={e => setSkabelonForm(f => ({ ...f, indhold: e.target.value }))}
                            rows={8} placeholder="Skriv kontrakttekst her..."
                            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-[13px] text-foreground focus:outline-none focus:ring-1 focus:ring-[#FF2A6D] resize-y" />
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-5">
                        <button onClick={() => setSkabelonDialog(null)} className="px-4 py-2 rounded-xl border border-border text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">Annuller</button>
                        <button onClick={skabelonDialog.mode === "opret" ? doOpretSkabelon : doOpdaterSkabelon}
                          disabled={skabelonSaving || !skabelonForm.navn.trim()}
                          className="px-4 py-2 rounded-xl bg-[#FF2A6D] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                          {skabelonSaving ? "Gemmer..." : skabelonDialog.mode === "opret" ? "Opret" : "Gem"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bekræft slet skabelon */}
                {skabelonConfirmSlet && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSkabelonConfirmSlet(null)}>
                    <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
                      <h3 className="text-base font-bold mb-2">Slet skabelon?</h3>
                      <p className="text-[13px] text-muted-foreground mb-4">Er du sikker på at du vil slette <strong>{skabelonConfirmSlet.navn}</strong>?</p>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setSkabelonConfirmSlet(null)} className="px-4 py-2 rounded-xl border border-border text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">Annuller</button>
                        <button onClick={() => doSletSkabelon(skabelonConfirmSlet)} className="px-4 py-2 rounded-xl bg-red-500 text-white text-[13px] font-semibold hover:opacity-90 transition-opacity">Slet</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Medarbejderliste (skjult på indstillinger-tab) ── */}
        {tab !== "indstillinger" && (loading ? (
          <div className="flex flex-col items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground mt-3">Henter medarbejdere...</p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Inbox className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">Ingen medarbejdere fundet</p>
          </div>
        ) : (
          <>
          {/* Desktop: tabel. Skjult på mobil til fordel for kort-layout nedenfor. */}
          <div className="hidden sm:block rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider w-10"></th>
                  <Th col="name" label="Navn" />
                  <Th col="stilling" label="Stilling" />
                  <Th col="afdeling" label="By" />
                  <Th col="rolle" label="Rolle" />
                  <Th col="arbejds_telefon" label="Firma telefon" />
                  <Th col="arbejds_email" label="Firma e-mail" />
                  {/* Status vises kun for brugere med rettigheder (superadmin/admin/
                      kan_se_medarbejdere) — ikke for menige medarbejdere. */}
                  {canSeStamdata && (
                    <th className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  )}
                  {canSeStamdata && (
                    <Th col="last_login" label="Seneste login" />
                  )}
                  <th className="py-3 px-4 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((m, i) => {
                  // #38.5: kommende ansat vises som "Starter [dato]" (blå).
                  const s = m.er_kommende ? STATUS_STYLES["Starter"] : STATUS_STYLES[m.status];
                  return (
                    <tr key={m.item_id || m.email}
                      className={`border-b border-border hover:bg-secondary/30 transition-colors ${i % 2 === 0 ? "" : "bg-secondary/10"}`}>
                      <td className="py-2 px-4">
                        {m.image_file_id ? (
                          <img src={`${API}/podio/image/${m.image_file_id}`} alt={m.name}
                            onClick={() => canNavigate && m.item_id && navigate(`/admin/medarbejdere/${m.item_id}`)}
                            className={`w-8 h-8 rounded-full object-cover border border-border ${canNavigate && m.item_id ? "cursor-pointer" : ""}`} />
                        ) : (
                          <div onClick={() => canNavigate && m.item_id && navigate(`/admin/medarbejdere/${m.item_id}`)}
                            className={`w-8 h-8 rounded-full bg-secondary flex items-center justify-center border border-border ${canNavigate && m.item_id ? "cursor-pointer" : ""}`}>
                            <span className="text-[13px] font-semibold text-muted-foreground">{m.name?.charAt(0)?.toUpperCase() || "?"}</span>
                          </div>
                        )}
                      </td>
                      <td className={`py-3 px-4 font-medium ${canNavigate && m.item_id ? "cursor-pointer hover:text-[#FF2A6D] transition-colors" : ""}`}
                        onClick={() => canNavigate && m.item_id && navigate(`/admin/medarbejdere/${m.item_id}`)}>
                        {m.name || "—"}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{m.stilling || "—"}</td>
                      <td className="py-3 px-4 text-muted-foreground">{displayCity(m.afdeling)}</td>
                      <td className="py-3 px-4"><RolleBadge rolle={m.rolle} /></td>
                      <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                        {m.arbejds_telefon
                          ? <a href={`tel:${String(m.arbejds_telefon).replace(/\s+/g,"")}`} onClick={e => e.stopPropagation()} className="hover:text-[#FF2A6D] transition-colors">{m.arbejds_telefon}</a>
                          : "—"}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground max-w-[200px] truncate">
                        {m.arbejds_email
                          ? <a href={`mailto:${m.arbejds_email}`} onClick={e => e.stopPropagation()} className="hover:text-[#FF2A6D] transition-colors break-all">{m.arbejds_email}</a>
                          : "—"}
                      </td>
                      {canSeStamdata && (
                        <td className="py-3 px-4">
                          {s ? (
                            <span className={`inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-full px-2.5 py-1 ${s.bg} ${s.text}`}
                              title={m.er_kommende ? "Kontrakt underskrevet — adgang aktiveres automatisk på startdatoen" : undefined}>
                              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                              {m.er_kommende
                                ? `Starter ${fmtDatoShort(m.starter_dato)}`
                                : m.status === "Opsagt" && m.ophørsdato && m.ophørsdato > todayIso
                                ? `Opsagt d. ${fmtDatoShort(m.ophørsdato)}`
                                : m.status}
                            </span>
                          ) : (
                            <span className="text-[13px] text-muted-foreground">{m.status || "—"}</span>
                          )}
                        </td>
                      )}
                      {canSeStamdata && (
                        <td className="py-3 px-4 text-muted-foreground whitespace-nowrap text-[13px]">
                          {m.last_login ? fmtDatoShort(m.last_login) : "—"}
                        </td>
                      )}
                      <td className="py-3 px-4">
                        {/* #60: Genansæt synlig for Admin OG SuperAdmin; destruktive
                            handlinger (arkivér/gendan/slet) forbliver SuperAdmin. */}
                        {!(isAdmin || isSuperAdmin) || m._kun_login_bruger ? null : (
                          <div className="flex items-center justify-end gap-1">
                            {/* Genansæt — kun på Tidligere + Arkiv */}
                            {(tab === "tidligere" || tab === "arkiv") && (
                              <button onClick={e => { e.stopPropagation(); setGenansaetItem(m); }}
                                title="Genansæt medarbejderen"
                                className="flex items-center gap-1 px-2 py-1 rounded-full text-[13px] font-semibold text-[#FF2A6D] bg-[#FF2A6D]/10 hover:bg-[#FF2A6D]/20 transition-colors whitespace-nowrap">
                                <RotateCcw className="w-3.5 h-3.5" /> Genansæt
                              </button>
                            )}
                            {/* #61 DEL A: Ret rolle + Fjern fra medarbejdere — SuperAdmin, ikke i arkiv. */}
                            {isSuperAdmin && tab !== "arkiv" && (
                              <>
                                <button onClick={e => { e.stopPropagation(); setRettighedPopup(m); }}
                                  title="Ret rolle og rettigheder"
                                  className="p-1.5 rounded-full text-muted-foreground hover:text-[#FF2A6D] hover:bg-[#FF2A6D]/10 transition-colors">
                                  <Shield className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={e => { e.stopPropagation(); setConfirm({ type: "fjern", item: m }); }}
                                  title="Fjern fra medarbejdere — sæt tilbage til Elev"
                                  className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                  <UserMinus className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                            {!isSuperAdmin ? null : tab !== "arkiv" ? (
                              <button onClick={e => { e.stopPropagation(); setConfirm({ type: "arkiver", item: m }); }}
                                title="Flyt til arkiv"
                                className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <>
                                <button onClick={() => setConfirm({ type: "slet", item: m })} title="Slet permanent"
                                  className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {/* Mobil: kort-layout pr. medarbejder. Skjult fra sm: og op. */}
          <div className="sm:hidden flex flex-col gap-2">
            {sorted.map((m) => {
              const s = m.er_kommende ? STATUS_STYLES["Starter"] : STATUS_STYLES[m.status];
              return (
                <div key={m.item_id || m.email} className="rounded-xl border border-border bg-background p-3">
                  <div className="flex items-start gap-3">
                    {m.image_file_id ? (
                      <img src={`${API}/podio/image/${m.image_file_id}`} alt={m.name}
                        onClick={() => canNavigate && m.item_id && navigate(`/admin/medarbejdere/${m.item_id}`)}
                        className={`w-10 h-10 rounded-full object-cover border border-border shrink-0 ${canNavigate && m.item_id ? "cursor-pointer" : ""}`} />
                    ) : (
                      <div onClick={() => canNavigate && m.item_id && navigate(`/admin/medarbejdere/${m.item_id}`)}
                        className={`w-10 h-10 rounded-full bg-secondary flex items-center justify-center border border-border shrink-0 ${canNavigate && m.item_id ? "cursor-pointer" : ""}`}>
                        <span className="text-sm font-semibold text-muted-foreground">{m.name?.charAt(0)?.toUpperCase() || "?"}</span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`font-semibold truncate ${canNavigate && m.item_id ? "cursor-pointer" : ""}`}
                          onClick={() => canNavigate && m.item_id && navigate(`/admin/medarbejdere/${m.item_id}`)}>
                          {m.name || "—"}
                        </p>
                        {/* #60: Genansæt for Admin+SuperAdmin; destruktive kun SuperAdmin */}
                        {(isAdmin || isSuperAdmin) && !m._kun_login_bruger && (
                          <div className="flex items-center gap-1 shrink-0 -mr-1">
                            {(tab === "tidligere" || tab === "arkiv") && (
                              <button onClick={e => { e.stopPropagation(); setGenansaetItem(m); }}
                                title="Genansæt medarbejderen"
                                className="flex items-center gap-1 px-2 py-1 rounded-full text-[13px] font-semibold text-[#FF2A6D] bg-[#FF2A6D]/10 hover:bg-[#FF2A6D]/20 transition-colors whitespace-nowrap">
                                <RotateCcw className="w-3.5 h-3.5" /> Genansæt
                              </button>
                            )}
                            {/* #61 DEL A: Ret rolle + Fjern fra medarbejdere — SuperAdmin, ikke i arkiv. */}
                            {isSuperAdmin && tab !== "arkiv" && (
                              <>
                                <button onClick={e => { e.stopPropagation(); setRettighedPopup(m); }}
                                  title="Ret rolle og rettigheder"
                                  className="p-1.5 rounded-full text-muted-foreground hover:text-[#FF2A6D] hover:bg-[#FF2A6D]/10 transition-colors">
                                  <Shield className="w-4 h-4" />
                                </button>
                                <button onClick={e => { e.stopPropagation(); setConfirm({ type: "fjern", item: m }); }}
                                  title="Fjern fra medarbejdere — sæt tilbage til Elev"
                                  className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                  <UserMinus className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {!isSuperAdmin ? null : tab !== "arkiv" ? (
                              <button onClick={e => { e.stopPropagation(); setConfirm({ type: "arkiver", item: m }); }}
                                title="Flyt til arkiv"
                                className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <>
                                <button onClick={() => setConfirm({ type: "slet", item: m })} title="Slet permanent"
                                  className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-[13px] text-muted-foreground truncate">
                        {m.stilling || "—"}{m.afdeling ? ` · ${displayCity(m.afdeling)}` : ""}
                      </p>
                      {m.rolle && <div className="mt-1"><RolleBadge rolle={m.rolle} /></div>}
                      {canSeStamdata && (
                        <div className="mt-1.5">
                          {s ? (
                            <span className={`inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-full px-2.5 py-1 ${s.bg} ${s.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                              {m.er_kommende
                                ? `Starter ${fmtDatoShort(m.starter_dato)}`
                                : m.status === "Opsagt" && m.ophørsdato && m.ophørsdato > todayIso
                                ? `Opsagt d. ${fmtDatoShort(m.ophørsdato)}`
                                : m.status}
                            </span>
                          ) : (
                            <span className="text-[13px] text-muted-foreground">{m.status || "—"}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Kontaktinfo */}
                  {(m.arbejds_telefon || m.arbejds_email) && (
                    <div className="mt-2.5 pt-2.5 border-t border-border flex flex-col gap-1 text-[13px] text-muted-foreground">
                      {m.arbejds_telefon && (
                        <a href={`tel:${String(m.arbejds_telefon).replace(/\s+/g,"")}`} onClick={e => e.stopPropagation()}
                          className="hover:text-[#FF2A6D] transition-colors">{m.arbejds_telefon}</a>
                      )}
                      {m.arbejds_email && (
                        <a href={`mailto:${m.arbejds_email}`} onClick={e => e.stopPropagation()}
                          className="hover:text-[#FF2A6D] transition-colors break-all">{m.arbejds_email}</a>
                      )}
                    </div>
                  )}

                </div>
              );
            })}
          </div>
          </>
        ))}

        {isSuperAdmin && <LokationerAdmin token={token} />}
      </div>
    </div>
  );
}
